/**
 * The camera.
 *
 * One camera, looking down at the character from a distance somebody chooses, and
 * turned by dragging. It reads the world the way this kind of game is read: from
 * above and slightly behind, far enough out to see what is coming.
 *
 * There was a second one, over the shoulder, built for walking with the keys.
 * Both went together: held movement never sat well with a world where clicking
 * where to go is the whole of moving, and a camera built around it had nothing
 * left to do.
 */

import type { WorldPos } from '@inverse/common';
import * as THREE from 'three';

/**
 * Whether the touch interface is used.
 *
 * `auto` follows what the device says it has, which is right almost always. The
 * other two exist because a laptop with a touchscreen reports both, and because
 * testing the touch layout on a desktop is otherwise impossible.
 */
export type TouchMode = 'auto' | 'on' | 'off';

/**
 * Everything the player can set from the settings panel. Persisted as one blob in
 * local storage, so a field added here survives without any migration.
 */
export interface GameSettings {
  /** Whether dragging up looks up or down. */
  invertY: boolean;
  sensitivity: number;
  /** Draws the tile grid on the ground around the player. */
  showGrid: boolean;
  /** Outlines the tile the player is standing on, which is their actual position. */
  highlightTile: boolean;
  touch: TouchMode;

  /**
   * How much of the screen's resolution the world is drawn at.
   *
   * Below one it is drawn smaller and stretched, which is the cheapest way to buy
   * frames on a weak machine. Above one costs frames to buy sharper edges.
   */
  renderScale: number;
  /** How far the world is drawn before it fades into the distance, in tiles. */
  viewDistance: number;
  /**
   * Whether the camera may be pulled right back, for looking at the landscape.
   *
   * Off, the camera stays where the game is played from. On, it goes up to a
   * few hundred tiles, which is how a range or a coastline is judged: neither
   * is visible from the height a person stands at.
   */
  surveyCamera: boolean;
  /** Names over other players and characters. */
  showNames: boolean;
  /** The marker showing where a click sent the player. */
  showClickMarker: boolean;
  /**
   * Whether the sky follows the world's clock.
   *
   * Off holds it at noon, which is exactly the light the world had before there
   * was a clock. On is the world as it is meant to be read: something written to
   * happen at night should look like night while it happens.
   */
  dayAndNight: boolean;
  /**
   * Whether the world shifts so the character stays in the open part of it.
   *
   * On a small screen a panel really is in the way and this happens whatever is
   * set. On anything wider it is a preference: some people want the character
   * always in the clear, and some want the camera to sit perfectly still.
   */
  centreOnPlayer: boolean;
  /**
   * Whether panels float free of the bar.
   *
   * Off, one panel is open at a time above the buttons, the way it is on a
   * phone. On, each opens as a window that can be dragged anywhere and any
   * number of them can be up at once.
   */
  floatingPanels: boolean;
  /**
   * Whether the line saying what the pointer is on rides the pointer.
   *
   * Off, it sits wherever it has been put, which is a corner of the screen and
   * a glance away from what is being pointed at. On, it follows just above and
   * to the right of the pointer, so the name of the thing and the thing are in
   * the same place and neither is ever looked away from. It hides itself over
   * anything that is not the world.
   */
  targetOnPointer: boolean;
  /** How long the pointer rests on something before it says what it is, in milliseconds. */
  tooltipDelay: number;
  /**
   * Marks in the world showing where the followed quest step points.
   *
   * On by default and easily turned off: being shown where to go is help while
   * somebody is learning a place and clutter once they know it.
   */
  showQuestMarks: boolean;
  /**
   * The small things lying on the ground: grass, pebbles, sticks.
   *
   * Painted ground with nothing on it reads as a painted plane however good the
   * colour is, so this is on by default. It is built once with each square of
   * ground and drawn as a handful of instanced meshes, so it costs draw calls
   * and memory rather than frames, and it is the first thing worth turning off
   * on a machine that is short of either.
   */
  groundClutter: boolean;
  /**
   * Whether the world is a window of its own rather than the whole page.
   *
   * Off, the world fills the browser window and the panels float above it. On,
   * it becomes a panel with a bar to drag it by and a grip on every corner, and
   * everything else on the screen already moves, so the whole arrangement is
   * somebody's to make: the world in the middle and the panels beside it, or the
   * world in a corner while a map is being drawn on the rest of the screen.
   */
  gameViewPanel: boolean;
  /**
   * Following a quest the moment it is begun.
   *
   * Off by default, because being marked at is a choice. On, starting a quest
   * follows it without anybody pressing anything, which is what somebody who
   * always presses Follow actually wants.
   */
  followNewQuests: boolean;
  /**
   * Numbers that float up for experience as it is earned.
   *
   * On by default: what a swing was worth is the answer to the swing, and
   * reading it out of the chat log afterwards is not an answer. Off for anybody
   * who would rather watch the world than the numbers.
   */
  showXpDrops: boolean;
  /** A running count of what each skill has earned this sitting, and how fast. */
  xpTracker: boolean;
  /**
   * Whether requests from other people reach this player at all.
   *
   * Two of them, because they are two different intrusions: somebody who is
   * happy to be asked to trade may not want to be asked to be friends, and the
   * other way round. Off means the request is refused outright and whoever sent
   * it is told so, rather than being left waiting on an answer that will never
   * come.
   */
  takeFriendRequests: boolean;
  takeTradeRequests: boolean;
  /**
   * The map in the corner.
   *
   * On by default: knowing where you are is not an advanced need. Off for
   * anybody who wants the screen back, since everything it shows can also be
   * had from the whole-island map.
   */
  showMinimap: boolean;
  /**
   * Whether the corner map keeps north at the top.
   *
   * Off by default, so it turns with the camera the way a map held in the hand
   * turns with the person holding it. On for anybody who navigates by compass
   * and finds a turning map worse than useless.
   */
  minimapNorthUp: boolean;
  /**
   * A red square inside every tile nothing can stand on, while editing.
   *
   * Off by default and only drawn in editing mode: what is closed to walking is
   * invisible from the ground, so a tile left blocked after the wall came down
   * is the sort of mistake nobody finds until somebody walks into it.
   */
  showBlocked: boolean;
  /** Seconds of quiet before the chat log fades. Zero leaves it up. */
  chatFadeSeconds: number;
  /**
   * When roofs are taken away.
   *
   * A roof nobody can see under is a roof nobody builds. Under one is the
   * ordinary rule; hovering lifts the roofs near the pointer, like holding a
   * lamp over a model; both does both; and always leaves every roof off, which
   * is what building the inside of something wants.
   */
  roofs: RoofMode;
  /** How loud everything is, nought to one, and each kind of sound within that. */
  soundVolume: number;
  effectsVolume: number;
  interfaceVolume: number;
  ambientVolume: number;
  /** Nothing at all, whatever the sliders say. */
  muted: boolean;
  /**
   * Dropping anything worth more than this many coins asks first. Nought never
   * asks.
   */
  confirmDropsOver: number;
}

export type RoofMode = 'underneath' | 'hovered' | 'both' | 'always';

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  invertY: false,
  sensitivity: 1,
  showGrid: false,
  highlightTile: true,
  touch: 'auto',
  renderScale: 1,
  viewDistance: 130,
  surveyCamera: false,
  showNames: true,
  showClickMarker: true,
  dayAndNight: true,
  showQuestMarks: true,
  groundClutter: true,
  gameViewPanel: false,
  followNewQuests: false,
  showXpDrops: true,
  xpTracker: true,
  takeFriendRequests: true,
  takeTradeRequests: true,
  showMinimap: true,
  minimapNorthUp: false,
  showBlocked: false,
  centreOnPlayer: false,
  floatingPanels: false,
  targetOnPointer: false,
  tooltipDelay: 260,
  chatFadeSeconds: 9,
  roofs: 'underneath',
  soundVolume: 0.7,
  effectsVolume: 1,
  interfaceVolume: 0.6,
  ambientVolume: 0.5,
  muted: false,
  confirmDropsOver: 100,
};

/** How far above the ground the camera looks, so it frames the body and not the feet. */
const EYE_HEIGHT = 1.0;

/** Whether this device is one a finger is the main pointer on. */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches && navigator.maxTouchPoints > 0;
}

/**
 * Whether there is not much screen to work with.
 *
 * A different question from whether it is a touch device: a phone is both, a
 * laptop with a small browser window is only this, and a television is neither.
 * What follows from it is how much room the interface takes rather than how it
 * is pressed.
 */
export function isSmallScreen(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 940 || window.innerHeight < 620;
}

/** Whether the touch interface should be drawn, given the setting and the device. */
export function usingTouch(settings: GameSettings): boolean {
  if (settings.touch === 'on') return true;
  if (settings.touch === 'off') return false;
  return isTouchDevice();
}

/**
 * How far above the ground the camera is held, and how near the player it may
 * be pulled to stay there.
 *
 * Clear enough that a hillside between the two does not fill the screen with
 * rock, and near enough that a player backed into a corner is still seen rather
 * than lost inside their own head.
 */
const CAMERA_CLEARANCE = 0.9;

/** How far back the survey camera goes, in tiles. */
const SURVEY_DISTANCE = 420;
const CAMERA_NEAREST = 3;

/** How far out and how steep the camera may be taken. */
const LIMITS = {
  minDistance: 8,
  maxDistance: 34,
  distance: 18,
  /** Straight down is 1.57; the limits keep the angle between steep and low. */
  minPitch: 0.45,
  maxPitch: 1.45,
  pitch: 1.05,
};

export class CameraController {
  /**
   * Where the camera rests: looking north, with east on the right.
   *
   * At nought the camera sits south of what it is looking at and faces along
   * negative Z. In a right handed world with Y up that puts positive X on the
   * right of the screen, which is east, and the two agree without anything
   * being flipped anywhere else. It used to rest at half a turn from here,
   * facing positive Z, which put positive X on the left: the maps then had to
   * draw east on the left to match, and the editor had to relabel the sides of
   * a tile to stop the words being visibly wrong.
   */
  yaw = 0;
  pitch = LIMITS.pitch;
  distance = LIMITS.distance;

  private settings: GameSettings = { ...DEFAULT_GAME_SETTINGS };
  private readonly desired = new THREE.Vector3();
  private readonly focus = new THREE.Vector3();

  /**
   * Where on the canvas the player should appear, in pixels.
   *
   * Interfaces cover parts of the screen, and the character standing behind one is
   * worse than a smaller panel would be. Rather than shrinking the interface, the
   * lens is shifted so the player sits in the middle of whatever is left. Null
   * means the middle of the canvas, which is what a desktop wants.
   */
  private frame: { x: number; y: number; width: number; height: number } | null = null;

  constructor(private readonly camera: THREE.PerspectiveCamera) {}

  apply(settings: GameSettings): void {
    this.settings = { ...settings };
  }

  /** Re-applies the lens shift after the window changed size. */
  resized(): void {
    this.applyFrame();
  }

  /**
   * Turns the camera: dragging sideways swings around the player, and dragging up
   * and down raises or lowers the angle they are watched from.
   */
  rotate(deltaX: number, deltaY: number): void {
    const speed = 0.0035 * this.settings.sensitivity;
    this.yaw -= deltaX * speed;

    const direction = this.settings.invertY ? -1 : 1;
    this.pitch = clamp(this.pitch + deltaY * speed * direction, LIMITS.minPitch, LIMITS.maxPitch);
  }

  /** Turning from a held key, which is measured in seconds rather than pixels. */
  turn(yawPerSecond: number, pitchPerSecond: number, delta: number): void {
    if (yawPerSecond === 0 && pitchPerSecond === 0) return;
    this.yaw -= yawPerSecond * delta;

    this.pitch = clamp(this.pitch + pitchPerSecond * delta, LIMITS.minPitch, LIMITS.maxPitch);
  }

  /**
   * The part of the canvas the player should be centred in.
   *
   * Applied as a lens shift rather than by aiming somewhere else: the projection
   * itself moves, so everything derived from it moves with it. That matters more
   * than the framing does, because picking a tile under a finger runs through the
   * same projection, and a camera that merely looks somewhere else would put the
   * tile the player tapped under a different pixel.
   */
  setFrame(frame: { x: number; y: number; width: number; height: number } | null): void {
    this.frame = frame && frame.width > 0 && frame.height > 0 ? { ...frame } : null;
    this.applyFrame();
  }

  private applyFrame(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const frame = this.frame;

    if (!frame) {
      this.camera.clearViewOffset();
      return;
    }

    // Where the middle of the free area is, against the middle of the canvas.
    const shiftX = width / 2 - (frame.x + frame.width / 2);
    const shiftY = height / 2 - (frame.y + frame.height / 2);

    // The window is the same size as the frame it is taken from, so this shifts
    // the lens without zooming. Moving the frustum towards the free area is what
    // brings the player into it.
    this.camera.setViewOffset(width, height, shiftX, shiftY, width, height);
  }

  /**
   * How far back the camera may be taken.
   *
   * The play distance is a few tiles up and a few tiles back, which is the only
   * place the game is actually played from and the only place worth judging how
   * it looks. Building a continent is a different job: a valley reads as a
   * valley from two hundred tiles up and as a green wall from twenty, and a
   * landscape cannot be designed through a keyhole. So there is a survey
   * distance as well, and it is a setting rather than a mode, because anybody
   * who wants to look at the world from the air should be allowed to.
   */
  private get furthest(): number {
    return this.settings.surveyCamera ? SURVEY_DISTANCE : LIMITS.maxDistance;
  }

  zoom(delta: number): void {
    const step = this.distance > LIMITS.maxDistance ? 0.08 : 0.01;
    this.distance = clamp(this.distance + delta * step, LIMITS.minDistance, this.furthest);
  }

  /**
   * Movement direction for held input, rotated into the camera's frame so that
   * forward means away from the camera regardless of which way it is facing.
   */
  movementBasis(): { forwardX: number; forwardZ: number; rightX: number; rightZ: number } {
    return {
      forwardX: -Math.sin(this.yaw),
      forwardZ: -Math.cos(this.yaw),
      rightX: Math.cos(this.yaw),
      rightZ: -Math.sin(this.yaw),
    };
  }

  /**
   * Places the camera for this frame.
   *
   * The focus point is smoothed rather than pinned to the player, so the small
   * corrections that prediction applies are not visible as camera jitter.
   */
  update(
    target: WorldPos,
    groundHeight: number,
    delta: number,
    /**
     * How high the ground stands at a point, for keeping the camera out of it.
     *
     * Given rather than worked out here, because the camera knows about angles
     * and the scene knows about ground.
     */
    groundAt?: (x: number, z: number) => number,
  ): void {
    this.focus.lerp(
      new THREE.Vector3(target.x, groundHeight + EYE_HEIGHT, target.z),
      1 - Math.pow(0.0001, delta),
    );

    const horizontal = Math.cos(this.pitch) * this.distance;
    this.desired.set(
      this.focus.x + Math.sin(this.yaw) * horizontal,
      this.focus.y + Math.sin(this.pitch) * this.distance,
      this.focus.z + Math.cos(this.yaw) * horizontal,
    );

    if (groundAt) this.keepOutOfTheGround(groundAt);

    this.camera.position.copy(this.desired);
    this.camera.lookAt(this.focus);
  }

  /**
   * Pulls the camera in until nothing solid lies between it and the player.
   *
   * Standing at the foot of a cliff with the camera swung round to face it, the
   * camera was inside the cliff and then under it: the world was drawn from a
   * point below the ground, the hillside was seen from beneath, and the whole
   * of it read as a fault in the world rather than as a camera in the wrong
   * place.
   *
   * The line from the player to where the camera wants to be is walked, and the
   * first point where the ground stands above that line is where the camera
   * stops. Then whatever is left is lifted clear of the ground under it, which
   * is the case the walk cannot catch: a camera directly over a slope that
   * rises faster than the line does.
   */
  private keepOutOfTheGround(groundAt: (x: number, z: number) => number): void {
    const away = this.desired.clone().sub(this.focus);
    const span = away.length();
    if (span < 0.001) return;

    const steps = Math.max(4, Math.ceil(span / 1.5));
    let held = 1;

    for (let step = 1; step <= steps; step++) {
      const share = step / steps;
      const x = this.focus.x + away.x * share;
      const z = this.focus.z + away.z * share;
      const y = this.focus.y + away.y * share;

      if (y - groundAt(x, z) >= CAMERA_CLEARANCE) continue;

      // Back to the last point that was clear, and no nearer than the closest
      // the camera is ever allowed to sit.
      held = Math.max((step - 1) / steps, CAMERA_NEAREST / span);
      break;
    }

    if (held < 1) this.desired.copy(this.focus).addScaledVector(away, held);

    const under = groundAt(this.desired.x, this.desired.z) + CAMERA_CLEARANCE;
    if (this.desired.y < under) this.desired.y = under;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
